import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ArrowUpRight, Eye, EyeOff, HandHeart, Link2, Loader2, Shield } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useSignUp } from '@/hooks/useSignUp';
import { getGoogleOAuthUrl } from '@/lib/oauth/google';
import { persistSignupConsent } from '@/lib/signupConsent';
import { showWarningToast } from '@/utils/toast-utils';
import { legalDocuments, type LegalDocument } from '@/pages/legal/legalDocuments';
import legalDocumentStyles from '@/pages/legal/LegalDocumentPage.module.css';
import nutritionistSignupVisual from '@/assets/images/appointments/nutritionist-signup.svg';
import nutritionOnboarding from '@/assets/images/nutrition_onboarding.svg';
import menuBarLogo from '@/assets/images/menu-bar-logo.svg';
import googleOAuthIcon from '@/assets/images/auth/google-oauth.svg';
import headlinesLogo from '@/assets/images/headlines-logo.svg';

export { SIGNUP_COMPLETION_PATH } from '@/hooks/useSignUp';

const authBackground = '/images/auth/background.svg';

const onboardingSteps = [
  {
    title: 'Step 1',
    heading: 'Tell us about your practice',
    description: 'Share quick details so we can tailor the platform to your workflow.',
    icon: Link2,
  },
  {
    title: 'Step 2',
    heading: 'See your personalised dashboard',
    description: 'Your workspace is ready with structured insights and tailored workflows.',
    icon: HandHeart,
  },
  {
    title: 'Step 3',
    heading: 'Add your first client',
    description: 'Set up their profile to initiate planning, monitoring, and compliance tracking.',
    icon: Shield,
  },
] as const;

const signupConsentFieldNames = ['acceptTerms', 'acceptPrivacy'] as const;

const extractLegalDocumentBodyHtml = (html: string) => {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1] ?? html;
};

interface LegalDocumentTextButtonProps {
  children: React.ReactNode;
  onOpen: () => void;
}

const LegalDocumentTextButton: React.FC<LegalDocumentTextButtonProps> = ({ children, onOpen }) => (
  <button
    type="button"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpen();
    }}
    className="inline p-0 text-left align-baseline font-medium text-slate-700 underline hover:text-[#0f7fa4]"
  >
    {children}
  </button>
);

const SignUp = () => {
  const navigate = useNavigate();
  const [activeLegalDocument, setActiveLegalDocument] = React.useState<LegalDocument | null>(null);

  const {
    form,
    step,
    verificationEmail,
    otp,
    otpRefs,
    showPassword,
    showConfirmPassword,
    isSubmitting,
    isVerifyingOtp,
    isResendingOtp,
    passwordsMatch,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerifyOtp,
    handleResendOtp,
    goBackToRegistration,
    onSubmit,
  } = useSignUp();
  const allSignupConsentsAccepted = signupConsentFieldNames.every((name) => form.watch(name));
  const activeLegalDocumentHtml = React.useMemo(
    () => activeLegalDocument ? extractLegalDocumentBodyHtml(activeLegalDocument.html) : '',
    [activeLegalDocument]
  );

  const handleGoogleOAuth = React.useCallback(() => {
    if (!allSignupConsentsAccepted) {
      const firstMissingConsent = signupConsentFieldNames.find((name) => !form.getValues(name));
      if (firstMissingConsent) form.setFocus(firstMissingConsent);
      showWarningToast(
        'Consent required',
        'Please accept all consent checkboxes before continuing with Google.'
      );
      return;
    }

    const googleOAuthUrl = getGoogleOAuthUrl({ includeSignupConsent: true });
    if (!googleOAuthUrl) {
      showWarningToast(
        'Google sign-in not configured',
        'Please set VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_REDIRECT_URI.'
      );
      return;
    }

    persistSignupConsent();
    window.location.href = googleOAuthUrl;
  }, [allSignupConsentsAccepted, form]);

  if (step === 'success') {
    return (
      <div className="min-h-dvh overflow-x-hidden bg-[#e9edf1] p-3 sm:p-5 2xl:p-8">
        <section
          className="relative mx-auto flex min-h-[calc(100dvh-1.5rem)] w-full overflow-hidden rounded-2xl border border-slate-200 px-4 py-5 shadow-[0_24px_80px_rgba(2,35,48,0.14)] sm:min-h-[calc(100dvh-2.5rem)] sm:px-6 sm:py-6 lg:px-8 lg:py-6 2xl:min-h-[calc(100dvh-4rem)] 2xl:px-14 2xl:py-12"
          style={{
            backgroundImage: `url(${authBackground})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <div className="relative z-10 flex w-full flex-col">
            <div className="mx-auto text-center text-white">
              <Link
                to="/"
                aria-label="CalorieScience home"
                className="mb-4 flex items-center justify-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 2xl:mb-6"
              >
                <img src={headlinesLogo} alt="" aria-hidden="true" className="h-9 w-8 sm:h-10 sm:w-9 2xl:h-12 2xl:w-10" />
                <div className="text-left">
                  <p className="text-3xl font-semibold leading-none tracking-tight sm:text-4xl 2xl:text-5xl">CalorieScience</p>
                  <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.22em] text-white/80 sm:text-xs">
                    Personalized. Intelligent. Scalable.
                  </p>
                </div>
              </Link>
              <h1 className="text-2xl font-semibold leading-tight sm:text-3xl 2xl:text-4xl">Welcome to CalorieScience</h1>
              <p className="mt-3 text-xs font-medium uppercase tracking-[0.2em] text-white/85 2xl:mt-5 sm:text-sm">
                You are all set. What&apos;s next?
              </p>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3 2xl:mt-10 2xl:gap-6">
              {onboardingSteps.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-[0_12px_36px_rgba(2,60,89,0.18)]">
                    <div className="px-5 pb-5 pt-5 sm:px-6 md:min-h-[220px] 2xl:min-h-[300px]">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#56BFE2]">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h2 className="mt-4 text-[29px] font-semibold leading-[1.1] text-slate-900 sm:text-[34px] 2xl:text-[38px]">{item.title}</h2>
                      <p className="mt-3 text-base leading-7 text-slate-700 lg:text-[17px] 2xl:text-[19px] 2xl:leading-8">{item.heading}</p>
                    </div>
                    <div className="flex flex-1 items-start border-t border-[#E6F0F5] bg-[#E4F9FF] px-5 py-5 sm:px-6 2xl:py-7">
                      <p className="text-[14px] leading-6 text-slate-700 sm:text-[15px] sm:leading-7 2xl:text-base 2xl:leading-8">{item.description}</p>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="mt-5 flex justify-center 2xl:mt-10">
              <Button
                type="button"
                onClick={() => navigate('/onboarding/practice-type/institution')}
                className="!h-[56px] !w-[266px] !rounded-[8px] !bg-white !px-[80px] !py-[16px] !text-xl !font-semibold !text-[#159FCB] hover:!bg-[#f3fbff]"
              >
                Continue
                <ArrowUpRight className="ml-2.5 h-6 w-6" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="h-dvh overflow-hidden bg-[#e9edf1] p-3 sm:p-5 lg:p-6">
      <div className="grid h-full min-h-0 w-full grid-cols-1 grid-rows-1 overflow-hidden rounded-2xl border border-slate-200 lg:grid-cols-2">
        <aside className="relative hidden min-h-0 overflow-hidden lg:block">
          <img
            src={nutritionistSignupVisual}
            alt="Scale your nutrition practice with intelligent meal planning"
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-8 xl:p-12">
            <h2 className="max-w-[560px] pt-4 text-[clamp(2.4rem,4vw,4.8rem)] font-medium leading-[0.98] text-white">
              <span>Scale your nutrition</span>
              <br />
              <span>practice 3X</span>
            </h2>

            <div className="flex flex-1 items-center justify-center">
              <img src={nutritionOnboarding} alt="" aria-hidden="true" className="max-h-[72%] w-auto max-w-[94%] object-contain" />
            </div>

            <div className="max-w-[560px] pb-4">
              <p className="text-[clamp(2rem,3vw,3.25rem)] font-medium leading-[1.08] text-white">
                Intelligent Meal Planning
              </p>
              <p className="mt-3 text-[clamp(1.15rem,1.55vw,2rem)] leading-[1.35] text-white/75">
                Create personalized, condition-specific meal plans in minutes - not hours.
              </p>
            </div>
          </div>
        </aside>

        <main
          className={`flex min-h-0 w-full justify-center overflow-y-auto bg-white p-4 sm:p-5 lg:p-8 ${
            step === 'verify' ? 'items-center' : 'items-start'
          }`}
        >
          <div className="w-full max-w-[460px]">
            <Link
              to="/"
              aria-label="CalorieScience home"
              className="mb-4 block w-fit rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC] focus-visible:ring-offset-2"
            >
              <img src={menuBarLogo} alt="Calorie Science" className="h-12 w-auto max-w-[260px]" />
            </Link>

            {step === 'verify' ? (
              <form
                className="space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleVerifyOtp();
                }}
              >
                <h1 className="text-4xl font-semibold leading-tight text-slate-800">Verify your email</h1>
                <p className="text-sm leading-6 text-slate-500">
                  Enter the six-digit code sent to {verificationEmail}.
                </p>

                <div className="grid grid-cols-6 gap-2 sm:flex sm:items-center sm:gap-3">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(element) => {
                        otpRefs.current[index] = element;
                      }}
                      value={digit}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="done"
                      maxLength={6}
                      pattern="[0-9]*"
                      onChange={(event) => handleOtpChange(index, event.target.value)}
                      onKeyDown={(event) => handleOtpKeyDown(index, event)}
                      onPaste={handleOtpPaste}
                      className="!h-11 !w-full !rounded-md !border-slate-300 !bg-white !p-0 text-lg font-semibold sm:!h-12 sm:!w-12 [&_.MuiOutlinedInput-input]:!p-0 [&_.MuiOutlinedInput-input]:!text-center"
                      aria-label={`OTP digit ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  disabled={isVerifyingOtp}
                  className="!h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                >
                  {isVerifyingOtp ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </Button>

                <p className="text-sm font-medium text-slate-600">
                  Didn&apos;t receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isResendingOtp}
                    className="font-semibold text-[#0f7fa4] underline disabled:opacity-60"
                  >
                    {isResendingOtp ? 'Resending...' : 'Resend'}
                  </button>
                </p>

                <p className="text-center text-sm text-slate-600">
                  Wrong email?{' '}
                  <button type="button" onClick={goBackToRegistration} className="font-medium text-[#3198c4] hover:underline">
                    Go back
                  </button>
                </p>
              </form>
            ) : (
              <>
                <div className="mt-5 space-y-3">
                  <button
                    type="button"
                    onClick={handleGoogleOAuth}
                    disabled={isSubmitting}
                    className="flex min-h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-500 bg-white px-5 py-3 text-base font-medium text-slate-700 transition-colors hover:border-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20AEDC] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <img src={googleOAuthIcon} alt="" aria-hidden="true" className="h-6 w-6 shrink-0" />
                    <span>Continue with Google</span>
                  </button>
                </div>

                <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span>or</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2.5">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
                            Full Name*
                          </FormLabel>
                          <FormControl>
                            <Input
                              autoComplete="name"
                              placeholder="Enter your full name"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
                            Email*
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              autoComplete="email"
                              placeholder="Enter your email"
                              className="!h-12 !rounded-md !border-slate-300 !bg-white text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
                            Password*
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                placeholder="********"
                                className="!h-12 !rounded-md !border-slate-300 !bg-white pr-10 text-sm"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  if (form.watch('confirmPassword')) {
                                    form.trigger('confirmPassword');
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
                            Confirm Password*
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                                placeholder="********"
                                className="!h-12 !rounded-md !border-slate-300 !bg-white pr-10 text-sm"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  if (form.watch('password')) {
                                    form.trigger('confirmPassword');
                                  }
                                }}
                                onBlur={() => {
                                  field.onBlur();
                                  form.trigger('confirmPassword');
                                }}
                              />
                              <button
                                type="button"
                                onClick={toggleConfirmPasswordVisibility}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                                aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                              >
                                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="acceptPrivacy"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <div className="flex items-start gap-3 px-1 py-1.5">
                            <FormControl>
                              <Checkbox
                                ref={field.ref}
                                checked={field.value}
                                onCheckedChange={(value) => field.onChange(value === true)}
                                className="mt-[8px] h-5 w-5 rounded-md border-slate-400 data-[state=checked]:border-[#20AEDC] data-[state=checked]:bg-[#20AEDC]"
                              />
                            </FormControl>
                            <FormLabel className="!mt-0 flex-1 text-[13px] font-normal leading-5 text-slate-600">
                              I confirm that I have read the{' '}
                              <LegalDocumentTextButton
                                onOpen={() => setActiveLegalDocument(legalDocuments.practitionerDeclarationConsent)}
                              >
                                Practitioner Declaration and Consent
                              </LegalDocumentTextButton>
                              . By checking the box, I consent to the declarations and acknowledge that my acceptance will be recorded electronically.
                            </FormLabel>
                          </div>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <div className="flex items-start gap-3 px-1 py-1.5">
                            <FormControl>
                              <Checkbox
                                ref={field.ref}
                                checked={field.value}
                                onCheckedChange={(value) => field.onChange(value === true)}
                                className="mt-[8px] h-5 w-5 rounded-md border-slate-400 data-[state=checked]:border-[#20AEDC] data-[state=checked]:bg-[#20AEDC]"
                              />
                            </FormControl>
                            <FormLabel className="!mt-0 flex-1 text-[13px] font-normal leading-5 text-slate-600">
                              I have read and agree to the{' '}
                              <LegalDocumentTextButton
                                onOpen={() => setActiveLegalDocument(legalDocuments.platformTerms)}
                              >
                                General Terms and Conditions
                              </LegalDocumentTextButton>
                              ,{' '}
                              <LegalDocumentTextButton
                                onOpen={() => setActiveLegalDocument(legalDocuments.dataProcessingAgreement)}
                              >
                                Data Processing Agreement
                              </LegalDocumentTextButton>
                              ,{' '}
                              <LegalDocumentTextButton
                                onOpen={() => setActiveLegalDocument(legalDocuments.platformPrivacy)}
                              >
                                Privacy Policy
                              </LegalDocumentTextButton>{' '}
                              and other applicable agreements or policies.
                            </FormLabel>
                          </div>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <div className="rounded-md bg-[#D8FFFC] px-4 py-3">
                      <p className="text-xs font-semibold text-[#3a7080]">Try it free for 31 days</p>
                      <p className="mt-1 text-xs text-[#4b7f8a]">Get full access and cancel anytime.</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={!passwordsMatch || !allSignupConsentsAccepted || isSubmitting}
                      className="!mt-3 !h-11 !w-full !rounded-md !bg-[#20AEDC] !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        'Sign Up'
                      )}
                    </Button>

                    <p className="pt-2 text-center text-sm text-slate-600">
                      Have an account?{' '}
                      <Link to="/login" className="font-semibold text-[#0f7fa4] hover:underline">
                        Login
                      </Link>
                    </p>

                  </form>
                </Form>
              </>
            )}
          </div>
        </main>
      </div>

      <Sheet
        open={Boolean(activeLegalDocument)}
        onOpenChange={(open) => {
          if (!open) {
            setActiveLegalDocument(null);
          }
        }}
      >
        <SheetContent
          side="bottom"
          className="max-h-[86dvh] overflow-hidden rounded-t-2xl border-slate-200 bg-white p-0 shadow-[0_-24px_70px_rgba(15,23,42,0.22)]"
        >
          {activeLegalDocument ? (
            <div className="flex max-h-[86dvh] flex-col">
              <SheetHeader className="border-b border-slate-200 px-5 py-4 text-left sm:px-7">
                <SheetTitle className="pr-9 text-xl font-semibold text-slate-900">
                  {activeLegalDocument.title}
                </SheetTitle>
                <SheetDescription className="text-sm text-slate-500">
                  {activeLegalDocument.documentMeta}
                </SheetDescription>
              </SheetHeader>

              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-4 py-4 sm:px-7 sm:py-6">
                <article className="mx-auto max-w-5xl rounded-lg border border-slate-200 bg-white shadow-sm">
                  <div
                    className={legalDocumentStyles.documentContent}
                    dangerouslySetInnerHTML={{ __html: activeLegalDocumentHtml }}
                  />
                </article>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SignUp;
